import type { Ref } from 'vue';
import type {
	AccommodationStrategy,
	DropTarget,
	GridBlockPosition,
	GridLayoutData,
	GridRow,
	OverlapValidationResult,
	PreviewPosition,
	ValidationResult,
} from './grid-types';
import { validateNoOverlaps } from './grid-overlap';
import { cleanupEmptyRows, redistributeRow } from './grid-row-management';
import { GRID_CONFIG } from './grid-types';

/**
 * Shared context for grid drag operations
 */
export interface GridDragContext {
	gridData: Ref<GridLayoutData>;
	validatePosition: (
		rowIndex: number,
		colStart: number,
		colSpan: number,
		excludeChildId?: string | null,
	) => ValidationResult;
	tryAccommodate: (
		childId: string,
		rowIndex: number,
		colStart: number,
		colSpan: number,
	) => AccommodationStrategy;
}

/**
 * Validates and applies grid data updates, rejecting changes that would cause overlaps.
 * Returns true if the update was applied, false if rejected.
 */
export function safeUpdateGridData(
	gridData: GridLayoutData,
	updates: Partial<GridLayoutData>,
	updateFn: (data: Partial<GridLayoutData>) => void,
	logPrefix = '[GridDrag]',
): boolean {
	const newPositions = updates.blockPositions ?? gridData.blockPositions;
	const newRows = updates.rows ?? gridData.rows;

	const validation: OverlapValidationResult = validateNoOverlaps(newPositions, newRows);

	if (!validation.valid) {
		console.warn(`${logPrefix} Update rejected: would cause overlap between blocks`, validation.conflictingIds);
		return false;
	}

	updateFn(updates);
	return true;
}

/**
 * Calculates an adaptive column span based on the target row's existing blocks.
 * When moving to a new row, the span is adjusted to distribute space fairly.
 */
export function getAdaptiveColSpan(
	gridData: GridLayoutData,
	childId: string,
	targetRowIndex: number,
	initialPosition: GridBlockPosition | null,
): number {
	// If moving to the same row, keep original span
	if (initialPosition && targetRowIndex === initialPosition.rowIndex) {
		return initialPosition.colSpan;
	}

	const row = gridData.rows[targetRowIndex];

	if (!row) {
		return initialPosition?.colSpan ?? GRID_CONFIG.COLUMNS;
	}

	// Count siblings in target row
	const siblingCount = Object.entries(gridData.blockPositions)
		.filter(([id, pos]) => id !== childId && pos.rowIndex === targetRowIndex)
		.length;

	const idealSpan = Math.floor(row.columns / (siblingCount + 1)) || 1;
	const originalSpan = initialPosition?.colSpan ?? GRID_CONFIG.COLUMNS;
	const boundedSpan = Math.min(originalSpan, row.columns, idealSpan);

	return Math.max(1, boundedSpan);
}

/**
 * Calculates the preview position for a drag operation.
 */
export function calculatePreviewPosition(
	ctx: GridDragContext,
	childId: string,
	initialPosition: GridBlockPosition | null,
	dropTarget: DropTarget | null,
	betweenRowHover: number | null,
): PreviewPosition | null {
	// Between-row hover takes priority
	if (betweenRowHover !== null) {
		return {
			rowIndex: betweenRowHover,
			colStart: 0,
			colSpan: GRID_CONFIG.COLUMNS,
			isNewRow: true,
			insertBeforeRow: betweenRowHover,
		};
	}

	if (!dropTarget) {
		return null;
	}

	const { rowIndex, colStart } = dropTarget;
	const adaptiveColSpan = getAdaptiveColSpan(ctx.gridData.value, childId, rowIndex, initialPosition);

	// New row at the end
	if (rowIndex >= ctx.gridData.value.rows.length) {
		return {
			rowIndex,
			colStart,
			colSpan: adaptiveColSpan,
			isNewRow: true,
		};
	}

	// Check if position is valid
	const validation = ctx.validatePosition(rowIndex, colStart, adaptiveColSpan, childId);

	if (validation.valid) {
		return {
			rowIndex,
			colStart,
			colSpan: adaptiveColSpan,
			isNewRow: false,
		};
	}

	// Try accommodation
	const accommodation = ctx.tryAccommodate(childId, rowIndex, colStart, adaptiveColSpan);

	if (accommodation.canAccommodate && accommodation.updatedPositions) {
		const newPos = accommodation.updatedPositions[childId];

		if (newPos) {
			return {
				rowIndex: newPos.rowIndex,
				colStart: newPos.colStart,
				colSpan: newPos.colSpan,
				isNewRow: false,
				accommodation,
			};
		}
	}

	if (accommodation.canAccommodate) {
		return {
			rowIndex,
			colStart,
			colSpan: adaptiveColSpan,
			isNewRow: accommodation.strategy === 'create_row',
			accommodation,
		};
	}

	// Fallback - show preview even if invalid
	return {
		rowIndex,
		colStart,
		colSpan: adaptiveColSpan,
		isNewRow: false,
	};
}

/**
 * Options for calculating drop updates
 */
export interface DropUpdateOptions {
	includeNewRows?: boolean;
	preserveEmptyRows?: boolean;
}

/**
 * Result of a drop update calculation
 */
export interface DropUpdateResult {
	rows: GridRow[];
	blockPositions: Record<string, GridBlockPosition>;
}

/**
 * Calculates the grid state after a drop operation.
 */
export function calculateDropUpdate(
	ctx: GridDragContext,
	childId: string,
	initialPosition: GridBlockPosition | null,
	dropTarget: DropTarget | null,
	betweenRowHover: number | null,
	options: DropUpdateOptions = {},
): DropUpdateResult | null {
	const { includeNewRows = true, preserveEmptyRows = false } = options;
	const gridData = ctx.gridData.value;

	// Handle between-row insertion
	if (betweenRowHover !== null) {
		if (!includeNewRows) return null;

		const insertIndex = betweenRowHover;
		const rows = [...gridData.rows];
		rows.splice(insertIndex, 0, { columns: GRID_CONFIG.COLUMNS });
		const positions = { ...gridData.blockPositions };

		// Shift blocks in rows >= insertIndex
		for (const [id, pos] of Object.entries(positions)) {
			if (id !== childId && pos.rowIndex >= insertIndex) {
				positions[id] = { ...pos, rowIndex: pos.rowIndex + 1 };
			}
		}

		positions[childId] = {
			rowIndex: insertIndex,
			colStart: 0,
			colSpan: GRID_CONFIG.COLUMNS,
		};

		// Redistribute old row if applicable
		if (initialPosition) {
			const oldRowIndex = initialPosition.rowIndex >= insertIndex
				? initialPosition.rowIndex + 1
				: initialPosition.rowIndex;

			if (oldRowIndex !== insertIndex) {
				redistributeRow(oldRowIndex, childId, positions, rows[oldRowIndex]?.columns);
			}
		}

		const cleaned = preserveEmptyRows
			? { rows, positions }
			: cleanupEmptyRows(rows, positions);

		return {
			rows: cleaned.rows,
			blockPositions: cleaned.positions,
		};
	}

	if (!dropTarget) return null;

	const { rowIndex, colStart } = dropTarget;
	const adaptiveColSpan = getAdaptiveColSpan(gridData, childId, rowIndex, initialPosition);

	// No change
	if (initialPosition && rowIndex === initialPosition.rowIndex && colStart === initialPosition.colStart) {
		return null;
	}

	// New row at the end
	if (rowIndex >= gridData.rows.length) {
		if (!includeNewRows) return null;

		const rows = [...gridData.rows, { columns: GRID_CONFIG.COLUMNS }];
		const positions: Record<string, GridBlockPosition> = { ...gridData.blockPositions, [childId]: {
			rowIndex: rows.length - 1,
			colStart,
			colSpan: adaptiveColSpan,
		} };

		if (initialPosition && initialPosition.rowIndex < gridData.rows.length) {
			redistributeRow(
				initialPosition.rowIndex,
				childId,
				positions,
				gridData.rows[initialPosition.rowIndex]?.columns,
			);
		}

		const cleaned = preserveEmptyRows
			? { rows, positions }
			: cleanupEmptyRows(rows, positions);

		return {
			rows: cleaned.rows,
			blockPositions: cleaned.positions,
		};
	}

	const newPosition: GridBlockPosition = {
		rowIndex,
		colStart,
		colSpan: adaptiveColSpan,
	};

	// Try valid position
	const validation = ctx.validatePosition(rowIndex, colStart, adaptiveColSpan, childId);

	if (validation.valid) {
		const positions: Record<string, GridBlockPosition> = {
			...gridData.blockPositions,
			[childId]: newPosition,
		};

		if (initialPosition && initialPosition.rowIndex !== rowIndex && initialPosition.rowIndex < gridData.rows.length) {
			redistributeRow(
				initialPosition.rowIndex,
				childId,
				positions,
				gridData.rows[initialPosition.rowIndex]?.columns,
			);
		}

		const cleaned = preserveEmptyRows
			? { rows: [...gridData.rows], positions }
			: cleanupEmptyRows([...gridData.rows], positions);

		return {
			rows: cleaned.rows,
			blockPositions: cleaned.positions,
		};
	}

	// Try accommodation
	const accommodation = ctx.tryAccommodate(childId, rowIndex, colStart, adaptiveColSpan);

	if (accommodation.canAccommodate) {
		const rows = accommodation.updatedRows ?? [...gridData.rows];
		const positions: Record<string, GridBlockPosition> = accommodation.updatedPositions
			? { ...accommodation.updatedPositions }
			: { ...gridData.blockPositions };

		// For shrink_all, the childId position is already set correctly inside
		// accommodation.updatedPositions (with proportional redistribution) — don't overwrite it.
		if (accommodation.strategy !== 'shrink_all') {
			positions[childId] = newPosition;
		}

		if (initialPosition && initialPosition.rowIndex !== rowIndex && initialPosition.rowIndex < gridData.rows.length) {
			redistributeRow(
				initialPosition.rowIndex,
				childId,
				positions,
				gridData.rows[initialPosition.rowIndex]?.columns,
			);
		}

		const cleaned = preserveEmptyRows
			? { rows, positions }
			: cleanupEmptyRows(rows, positions);

		return {
			rows: cleaned.rows,
			blockPositions: cleaned.positions,
		};
	}

	return null;
}

/**
 * Calculate the target column start index from a mouse X position within a row element.
 * Clamps the result to [0, rowColumns - colSpan].
 */
function calculateColStartFromMouse(
	mouseX: number,
	rowRect: DOMRect,
	rowColumns: number,
	colSpan: number,
): number {
	const cellWidth = rowRect.width / rowColumns;
	const relativeX = mouseX - rowRect.left;
	const targetColStart = Math.floor(relativeX / cellWidth);
	const maxStart = Math.max(0, rowColumns - colSpan);
	return Math.max(0, Math.min(targetColStart, maxStart));
}

/**
 * Finds the drop target based on mouse position over grid rows.
 * Includes hysteresis logic to prevent flickering when near row boundaries.
 */
export function findDropTargetInRows(
	ctx: GridDragContext,
	childId: string,
	initialPosition: GridBlockPosition | null,
	mouseX: number,
	mouseY: number,
	gridRows: HTMLElement[],
	lastStableRowIndex: number | null,
	lastStableMouseY: number | null,
	anchor?: {
		/** Original client X of the grabbed point, captured before preview layout changes. */
		initialMouseX: number;
		/** Horizontal distance from the dragged block's left edge to the grabbed point. */
		pointerOffsetX: number;
	},
): {
	target: DropTarget;
	newStableRowIndex: number;
	newStableMouseY: number;
} | null {
	const gridData = ctx.gridData.value;

	// Handle empty grid
	if (gridRows.length === 0) {
		return {
			target: { rowIndex: 0, colStart: 0, valid: true },
			newStableRowIndex: 0,
			newStableMouseY: mouseY,
		};
	}

	let targetRowIndex = initialPosition?.rowIndex ?? 0;
	let newStableRowIndex = lastStableRowIndex ?? targetRowIndex;
	let newStableMouseY = lastStableMouseY ?? mouseY;
	let foundRow = false;

	for (const [i, gridRow] of gridRows.entries()) {
		const rect = gridRow.getBoundingClientRect();

		if (mouseY >= rect.top && mouseY <= rect.bottom) {
			// Apply hysteresis for row switching
			if (lastStableRowIndex !== null && lastStableRowIndex !== i && lastStableMouseY !== null) {
				const movementRequired = Math.abs(mouseY - lastStableMouseY);

				if (movementRequired < GRID_CONFIG.ROW_HYSTERESIS_PX) {
					// Not enough movement — keep stable row
					targetRowIndex = lastStableRowIndex;
					foundRow = true;
					break;
				}
			}

			// Accept this row and update stable state
			targetRowIndex = i;
			newStableRowIndex = i;
			newStableMouseY = mouseY;
			foundRow = true;
			break;
		}
	}

	// Not over any row — fall back to stable row
	if (!foundRow && lastStableRowIndex !== null) {
		targetRowIndex = lastStableRowIndex;
	}

	// Compute colStart and validate once for the determined target row
	const adaptiveColSpan = getAdaptiveColSpan(gridData, childId, targetRowIndex, initialPosition);
	const targetRowEl = gridRows[targetRowIndex];
	const targetRowData = targetRowEl && gridData.rows[targetRowIndex];
	let targetColStart = initialPosition?.colStart ?? 0;

	if (targetRowData) {
		const rowRect = targetRowEl.getBoundingClientRect();

		if (anchor && initialPosition && targetRowIndex === initialPosition.rowIndex) {
			// Anchor to saved grid coordinates rather than rendered edges: gaps and fractional
			// bounds must not turn a stationary grab into a different grid column.
			const cellWidth = rowRect.width / targetRowData.columns;
			const deltaColumns = cellWidth > 0 ? Math.round((mouseX - anchor.initialMouseX) / cellWidth) : 0;
			const maxStart = Math.max(0, targetRowData.columns - adaptiveColSpan);
			targetColStart = Math.max(0, Math.min(initialPosition.colStart + deltaColumns, maxStart));
		}
		else {
			targetColStart = calculateColStartFromMouse(
				mouseX - (anchor?.pointerOffsetX ?? 0),
				rowRect,
				targetRowData.columns,
				adaptiveColSpan,
			);
		}
	}

	const accommodation = ctx.tryAccommodate(childId, targetRowIndex, targetColStart, adaptiveColSpan);
	const isValid = accommodation.canAccommodate;

	return {
		target: {
			rowIndex: targetRowIndex,
			colStart: targetColStart,
			valid: isValid,
		},
		newStableRowIndex,
		newStableMouseY,
	};
}
